import { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { ViewGridIcon, ViewListIcon } from "../Icons/Icons";

export const ViewSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme }) => theme.resolve("border_sm")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  overflow: hidden;
`;

export const ViewButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.resolve("spacing_md")};
  border: none;
  background: ${({ isActive, theme }) =>
    isActive ? theme.resolve("color_bg_surface_subtle") : "transparent"};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: ${({ theme }) => theme.resolve("spacing_5xl")};
  min-height: ${({ theme }) => theme.resolve("spacing_5xl")};
  box-shadow: ${({ isActive, theme }) =>
    isActive ? `0 1px 3px ${theme.resolve("color_neutral_gray300")}` : "none"};

  svg {
    width: ${({ theme }) => theme.resolve("spacing_3xl")};
    height: ${({ theme }) => theme.resolve("spacing_3xl")};
  }

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive
        ? theme.resolve("color_bg_surface_subtle")
        : theme.resolve("color_bg_surface_standard")};
  }
`;

export type ViewMode = "table" | "grid";

type ViewModeToggleProps = {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  storageKey?: string;
};

export function TableViewToggle({
  value,
  onChange,
  storageKey,
}: ViewModeToggleProps) {
  const theme = useTheme();

  const activeColor = theme.resolve("color_icon_secondary");
  const inactiveColor = theme.resolve("color_icon_disabled");

  useEffect(() => {
    if (!storageKey) return;

    const saved = sessionStorage.getItem(storageKey) as ViewMode | null;
    if (saved && saved !== value) {
      onChange(saved);
    }
  }, [onChange, storageKey, value]);

  const handleChange = (mode: ViewMode) => {
    onChange(mode);
    if (storageKey) {
      sessionStorage.setItem(storageKey, mode);
    }
  };

  return (
    <ViewSwitcher>
      <ViewButton
        isActive={value === "grid"}
        onClick={() => handleChange("grid")}
      >
        <ViewGridIcon fill={value === "grid" ? activeColor : inactiveColor} />
      </ViewButton>

      <ViewButton
        isActive={value === "table"}
        onClick={() => handleChange("table")}
      >
        <ViewListIcon fill={value === "table" ? activeColor : inactiveColor} />
      </ViewButton>
    </ViewSwitcher>
  );
}
